import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Divider,
  Stack,
  Card,
  CardContent,
  FormGroup,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete
} from '@mui/material';
// import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// import { TimePicker } from '@mui/x-date-pickers/TimePicker';

// Temporary replacement components to fix dayjs issues
const DatePicker = ({ value, onChange, disabled, slotProps, ...props }: any) => (
  <TextField
    type="date"
    value={value ? (typeof value === 'string' ? value : value.format?.('YYYY-MM-DD')) : ''}
    onChange={(e) => onChange?.(e.target.value)}
    disabled={disabled}
    fullWidth
    size="small"
    {...(slotProps?.textField || {})}
  />
);

const TimePicker = ({ value, onChange, disabled, slotProps, ...props }: any) => (
  <TextField
    type="time"
    value={value ? (typeof value === 'string' ? value : value.format?.('HH:mm')) : ''}
    onChange={(e) => onChange?.(e.target.value)}
    disabled={disabled}
    fullWidth
    size="small"
    {...(slotProps?.textField || {})}
  />
);
import { Save, Print, Download } from '@mui/icons-material';
import { format } from 'date-fns';
import UserSearchAutocomplete from '../UserSearchAutocomplete';
import { useAuth } from '../../hooks/useAuth';
import { useSiteContext } from '../../hooks/useSiteContext';
import { useSiteEquipment } from '../../hooks/useSiteEquipment';
import isolationService, { IsolationAnalysisResult } from '../../services/isolationService';
import permitService from '../../services/permitService';

interface AccessPermitFormProps {
  permitId?: string;
  initialData?: any;
  onSave?: (data: any, action?: string) => void;
  onGeneratePDF?: () => void;
  readOnly?: boolean;
}

interface IsolationRecord {
  id: string;
  sequence_number: number;
  equipment_code: string;
  isolation_point: string;
  isolation_method: string;
  lock_number: string;
  isolation_check_initial: string;
  issuer_initial: string;
  recipient_initial: string;
  amendment_issuer_initial: string;
  amendment_recipient_initial: string;
}

interface RecipientIsolationRecord {
  id: string;
  sequence_number: number;
  isolation_point: string;
  isolation_method: string;
  issuer_agreed_initial: string;
  lock_number: string;
  applied_initial: string;
  removed_initial: string;
}

interface WorkPartyRecord {
  id: string;
  sequence_letter: string;
  sign_on_date: Date | null;
  name: string;
  sign_on_signature: string;
  sign_off_signature: string;
  is_supervisor: boolean;
}

const AccessPermitForm: React.FC<AccessPermitFormProps> = ({
  permitId,
  initialData,
  onSave,
  onGeneratePDF,
  readOnly = false
}) => {
  const { user } = useAuth();
  const { currentSite } = useSiteContext();
  const { sites, selectedSite, setSelectedSite, equipment: siteEquipment, locations } = useSiteEquipment();
  
  // State for authorized recipients and auto-population tracking
  const [authorizedRecipients, setAuthorizedRecipients] = useState<any[]>([]);
  const [hasAutoPopulated, setHasAutoPopulated] = useState(false);
  
  // State for selected equipment and isolation analysis
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState<number[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [isolationAnalysis, setIsolationAnalysis] = useState<IsolationAnalysisResult | null>(null);
  const [loadingIsolations, setLoadingIsolations] = useState(false);

  // Work control number generation
  const generateWorkControlNumber = async (siteCode: string): Promise<string> => {
    try {
      // Get current year in YY format
      const year = new Date().getFullYear().toString().slice(-2);
      
      // Generate next sequence number by calling backend API
      const response = await permitService.getNextPermitSequence('access', siteCode);
      const sequenceNumber = response.next_sequence.toString().padStart(2, '0');
      
      return `${siteCode}-AP-${year}${sequenceNumber}`;
    } catch (error) {
      console.error('Error generating work control number:', error);
      // Fallback to timestamp-based number if API fails
      const year = new Date().getFullYear().toString().slice(-2);
      const timestamp = Date.now().toString().slice(-4);
      return `${siteCode}-AP-${year}${timestamp}`;
    }
  };

  // Basic permit information
  const [formData, setFormData] = useState({
    work_control_number: '',
    lockout_boxes: '',
    recipient_lock_numbers: '',
    recipient_name: '',
    recipient_company: '',
    recipient_contact_number: '',
    recipient_competency_expiry: null as Date | null,
    station: '',
    equipment_to_be_worked_on: '',
    work_to_be_done: '',
    but_note_that: '',
    
    // Issue section
    issuer_name: user?.full_name || '',
    issuer_competency_expiry: null as Date | null,
    issue_date: new Date(),
    issue_time: new Date(),
    issue_communication_hand: false,
    issue_communication_verbal: false,
    issue_communication_digital: false,
    
    // Status and workflow
    status: 'draft',
    
    // Return/Completion section (StayLive workflow)
    work_completed_successfully: false,
    equipment_returned_to_service: false,
    equipment_modifications_made: '',
    return_to_service_restrictions: '',
    return_reason: '',
    return_notes: '',
    
    // Cancellation
    cancellation_reason: ''
  });

  // Safety measures tables
  const [issuerSafetyMeasures, setIssuerSafetyMeasures] = useState<IsolationRecord[]>([]);
  const [recipientSafetyMeasures, setRecipientSafetyMeasures] = useState<RecipientIsolationRecord[]>([]);
  const [workPartyRecords, setWorkPartyRecords] = useState<WorkPartyRecord[]>([]);

  // Initialize empty rows
  useEffect(() => {
    if (issuerSafetyMeasures.length === 0) {
      const emptyIssuerRecords: IsolationRecord[] = Array.from({ length: 22 }, (_, index) => ({
        id: `issuer-${index + 1}`,
        sequence_number: index + 1,
        equipment_code: '',
        isolation_point: '',
        isolation_method: '',
        lock_number: '',
        isolation_check_initial: '',
        issuer_initial: '',
        recipient_initial: '',
        amendment_issuer_initial: '',
        amendment_recipient_initial: ''
      }));
      setIssuerSafetyMeasures(emptyIssuerRecords);
    }

    if (recipientSafetyMeasures.length === 0) {
      const emptyRecipientRecords: RecipientIsolationRecord[] = Array.from({ length: 17 }, (_, index) => ({
        id: `recipient-${index + 1}`,
        sequence_number: index + 1,
        isolation_point: '',
        isolation_method: '',
        issuer_agreed_initial: '',
        lock_number: '',
        applied_initial: '',
        removed_initial: ''
      }));
      setRecipientSafetyMeasures(emptyRecipientRecords);
    }

    if (workPartyRecords.length === 0) {
      const emptyWorkPartyRecords: WorkPartyRecord[] = Array.from({ length: 26 }, (_, index) => ({
        id: `work-party-${String.fromCharCode(97 + index)}`,
        sequence_letter: String.fromCharCode(97 + index), // a-z
        sign_on_date: null,
        name: '',
        sign_on_signature: '',
        sign_off_signature: '',
        is_supervisor: false
      }));
      setWorkPartyRecords(emptyWorkPartyRecords);
    }
  }, [issuerSafetyMeasures.length, recipientSafetyMeasures.length, workPartyRecords.length]);

  const handleFormChange = (field: string) => (event: any) => {
    const value = event.target?.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDateChange = (field: string) => (date: Date | null) => {
    setFormData(prev => ({ ...prev, [field]: date }));
  };

  // Equipment selection and isolation analysis
  const handleEquipmentSelection = useCallback(async (equipmentIds: number[]) => {
    setSelectedEquipmentIds(equipmentIds);
    
    if (equipmentIds.length === 0) {
      setIsolationAnalysis(null);
      // Reset isolation records to empty
      const emptyIssuerRecords: IsolationRecord[] = Array.from({ length: 22 }, (_, index) => ({
        id: `issuer-${index + 1}`,
        sequence_number: index + 1,
        equipment_code: '',
        isolation_point: '',
        isolation_method: '',
        lock_number: '',
        isolation_check_initial: '',
        issuer_initial: '',
        recipient_initial: '',
        amendment_issuer_initial: '',
        amendment_recipient_initial: ''
      }));
      setIssuerSafetyMeasures(emptyIssuerRecords);
      
      const emptyRecipientRecords: RecipientIsolationRecord[] = Array.from({ length: 17 }, (_, index) => ({
        id: `recipient-${index + 1}`,
        sequence_number: index + 1,
        isolation_point: '',
        isolation_method: '',
        issuer_agreed_initial: '',
        lock_number: '',
        applied_initial: '',
        removed_initial: ''
      }));
      setRecipientSafetyMeasures(emptyRecipientRecords);
      return;
    }

    try {
      setLoadingIsolations(true);
      const analysis = await isolationService.analyzeEquipmentIsolations(equipmentIds);
      setIsolationAnalysis(analysis);

      // Convert analysis to isolation records and populate the tables
      const issuerRecords = isolationService.convertAnalysisToIsolationRecords(analysis);
      const recipientRecords = isolationService.convertAnalysisToRecipientRecords(analysis);

      // Pad with empty records to maintain 22/17 row structure
      const paddedIssuerRecords: IsolationRecord[] = [
        ...issuerRecords,
        ...Array.from({ length: Math.max(0, 22 - issuerRecords.length) }, (_, index) => ({
          id: `issuer-empty-${index + 1}`,
          sequence_number: issuerRecords.length + index + 1,
          equipment_code: '',
          isolation_point: '',
          isolation_method: '',
          lock_number: '',
          isolation_check_initial: '',
          issuer_initial: '',
          recipient_initial: '',
          amendment_issuer_initial: '',
          amendment_recipient_initial: ''
        }))
      ];

      const paddedRecipientRecords: RecipientIsolationRecord[] = [
        ...recipientRecords,
        ...Array.from({ length: Math.max(0, 17 - recipientRecords.length) }, (_, index) => ({
          id: `recipient-empty-${index + 1}`,
          sequence_number: recipientRecords.length + index + 1,
          isolation_point: '',
          isolation_method: '',
          issuer_agreed_initial: '',
          lock_number: '',
          applied_initial: '',
          removed_initial: ''
        }))
      ];

      setIssuerSafetyMeasures(paddedIssuerRecords);
      setRecipientSafetyMeasures(paddedRecipientRecords);

      // Auto-populate equipment description
      const equipmentDescriptions = analysis.equipment_details.map(eq => 
        `${eq.kks_code} - ${eq.name}`
      ).join('\n');
      
      setFormData(prev => ({
        ...prev,
        equipment_to_be_worked_on: equipmentDescriptions,
        isolation_required: analysis.isolation_methods_count > 0
      }));

    } catch (error) {
      console.error('Failed to analyze isolations:', error);
      // Keep empty records on error
    } finally {
      setLoadingIsolations(false);
    }
  }, []);

  // Auto-populate station based on selected site
  useEffect(() => {
    if (selectedSite && !formData.station) {
      const site = sites.find(s => s.id === selectedSite);
      if (site) {
        setFormData(prev => ({ ...prev, station: site.name }));
      }
    }
  }, [selectedSite, formData.station, sites]);

  // Fetch authorized recipients for the current site
  useEffect(() => {
    const fetchAuthorizedRecipients = async () => {
      let siteId = currentSite?.siteId || selectedSite;
      if (!siteId) return;

      try {
        // Fetch users with recipient roles for this site
        const response = await fetch(`/api/v1/sites/users/site-roles/?site_id=${siteId}&role=recipient`, {
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.ok) {
          const data = await response.json();
          setAuthorizedRecipients(data.results || []);
        }
      } catch (error) {
        console.error('Error fetching authorized recipients:', error);
        setAuthorizedRecipients([]);
      }
    };

    fetchAuthorizedRecipients();
  }, [currentSite?.siteId, selectedSite]);

  // Auto-populate form data when creating a new permit
  useEffect(() => {
    const autoPopulateForm = async () => {
      // Only auto-populate once for new permits (no existing data)
      if (initialData?.id || hasAutoPopulated) return;

      console.log('🔧 Auto-populating form...', {
        currentSiteId: currentSite?.siteId,
        selectedSite,
        sitesLength: sites.length,
        user: user?.full_name,
        hasAutoPopulated
      });

      // Get current site information
      let currentSiteData = null;
      if (currentSite?.siteId) {
        currentSiteData = sites.find(site => site.id === currentSite.siteId);
      } else if (selectedSite) {
        currentSiteData = sites.find(site => site.id === selectedSite);
      }

      // Wait for site data and user to be available
      if (!currentSiteData || !user?.full_name || sites.length === 0) {
        console.log('🔧 Waiting for data...', { 
          hasSiteData: !!currentSiteData, 
          hasUser: !!user?.full_name, 
          sitesCount: sites.length 
        });
        return;
      }

      console.log('🔧 Site data found:', currentSiteData);

      try {
        console.log('🔧 Generating work control number for site:', currentSiteData.code);
        const workControlNumber = await generateWorkControlNumber(currentSiteData.code || 'UNK');
        
        console.log('🔧 Generated work control number:', workControlNumber);
        
        setFormData(prev => ({
          ...prev,
          work_control_number: workControlNumber,
          station: currentSiteData?.name || '',
          issuer_name: user?.full_name || '',
          issue_date: new Date(),
          issue_time: new Date(),
          status: 'draft'  // Start permits in draft status
        }));

        // Mark as auto-populated to prevent re-running
        setHasAutoPopulated(true);
        console.log('🔧 Auto-population completed');

      } catch (error) {
        console.error('Error auto-populating form:', error);
      }
    };

    autoPopulateForm();
  }, [currentSite?.siteId, selectedSite, sites, user?.full_name, initialData?.id, hasAutoPopulated]);

  const updateIssuerSafetyMeasure = (index: number, field: keyof IsolationRecord, value: string) => {
    setIssuerSafetyMeasures(prev => prev.map((record, i) => 
      i === index ? { ...record, [field]: value } : record
    ));
  };

  const updateRecipientSafetyMeasure = (index: number, field: keyof RecipientIsolationRecord, value: string) => {
    setRecipientSafetyMeasures(prev => prev.map((record, i) => 
      i === index ? { ...record, [field]: value } : record
    ));
  };

  const updateWorkPartyRecord = (index: number, field: keyof WorkPartyRecord, value: any) => {
    setWorkPartyRecords(prev => prev.map((record, i) => 
      i === index ? { ...record, [field]: value } : record
    ));
  };

  const handleSave = useCallback(() => {
    const permitData = {
      ...formData,
      isolation_records: [
        ...issuerSafetyMeasures.map(record => ({ ...record, record_type: 'issuer_applied' })),
        ...recipientSafetyMeasures.map(record => ({ ...record, record_type: 'recipient_applied' }))
      ],
      work_party_records: workPartyRecords
    };
    onSave?.(permitData);
  }, [formData, issuerSafetyMeasures, recipientSafetyMeasures, workPartyRecords, onSave]);

  const handleWorkflowAction = useCallback((action: string, newStatus: string) => {
    const permitData = {
      ...formData,
      status: newStatus,
      isolation_records: [
        ...issuerSafetyMeasures.map(record => ({ ...record, record_type: 'issuer_applied' })),
        ...recipientSafetyMeasures.map(record => ({ ...record, record_type: 'recipient_applied' }))
      ],
      work_party_records: workPartyRecords
    };
    onSave?.(permitData, action);
  }, [formData, issuerSafetyMeasures, recipientSafetyMeasures, workPartyRecords, onSave]);

  // Get available workflow actions based on current status
  const getWorkflowActions = () => {
    const status = formData.status;
    const actions = [];

    switch (status) {
      case 'draft':
        actions.push(
          { action: 'ready_for_issue', label: 'Ready for Issue', variant: 'contained' as const, color: 'primary' as const },
          { action: 'save_draft', label: 'Save Draft', variant: 'outlined' as const, color: 'primary' as const }
        );
        break;
      case 'planned':
        actions.push(
          { action: 'issue', label: 'Issue Permit', variant: 'contained' as const, color: 'success' as const },
          { action: 'back_to_draft', label: 'Back to Draft', variant: 'outlined' as const, color: 'secondary' as const }
        );
        break;
      case 'issued':
        actions.push(
          { action: 'return_permit', label: 'Return Permit', variant: 'contained' as const, color: 'success' as const },
          { action: 'suspend', label: 'Suspend', variant: 'outlined' as const, color: 'warning' as const }
        );
        break;
      case 'suspended':
        actions.push(
          { action: 'resume', label: 'Resume Work', variant: 'contained' as const, color: 'primary' as const },
          { action: 'return_permit', label: 'Return Permit', variant: 'outlined' as const, color: 'success' as const }
        );
        break;
      default:
        actions.push(
          { action: 'save_draft', label: 'Save', variant: 'contained' as const, color: 'primary' as const }
        );
    }

    return actions;
  };

  return (
    <Box sx={{ maxWidth: '210mm', mx: 'auto', p: 2 }}>
        {/* Status Indicator */}
      {formData.status && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Permit Status: <strong>{formData.status.charAt(0).toUpperCase() + formData.status.slice(1).replace(/_/g, ' ')}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formData.status === 'draft' && 'Permit is being prepared. Complete all required fields and mark as ready for issue.'}
            {formData.status === 'planned' && 'Permit is ready to be issued. Review and issue when appropriate.'}
            {formData.status === 'issued' && 'Permit has been issued. Work can proceed as authorized.'}
            {formData.status === 'suspended' && 'Permit is temporarily suspended. Work must stop until resumed.'}
            {formData.status === 'cancelled' && 'Permit has been returned/cancelled. Work is complete or terminated.'}
          </Typography>
          
          {/* Return Information for cancelled permits */}
          {formData.status === 'cancelled' && (formData.return_reason || formData.return_notes) && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Return Information:
              </Typography>
              {formData.return_reason && (
                <Typography variant="body2">
                  <strong>Reason:</strong> {formData.return_reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Typography>
              )}
              {formData.return_notes && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>Notes:</strong> {formData.return_notes}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Workflow Action Buttons */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        {!readOnly && getWorkflowActions().map((action) => (
          <Button
            key={action.action}
            variant={action.variant}
            color={action.color}
            startIcon={<Save />}
            onClick={() => {
              if (action.action === 'save_draft') {
                handleSave();
              } else {
                    const statusMap: Record<string, string> = {
                      ready_for_issue: 'planned',
                      issue: 'issued',
                      back_to_draft: 'draft',
                      return_permit: 'cancelled',  // StayLive: return = cancel
                      suspend: 'suspended',
                      resume: 'issued'
                    };
                handleWorkflowAction(action.action, statusMap[action.action] || formData.status);
              }
            }}
          >
            {action.label}
          </Button>
        ))}
        
        {/* Always show utility buttons */}
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={onGeneratePDF}
          disabled={!onGeneratePDF}
        >
          Download PDF
        </Button>
        <Button
          variant="outlined"
          startIcon={<Print />}
          onClick={() => window.print()}
        >
          Print
        </Button>
      </Stack>

      {/* Page 1: Basic Information */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        {/* StayLive Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
              STAY
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#FF6B35' }}>
              LIVE
            </Typography>
            <Typography variant="body2" sx={{ ml: 1 }}>
              Electrical Industry<br />Health & Safety Group
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Card sx={{ bgcolor: '#4CAF50', color: 'white', px: 2, py: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                Access Permit
              </Typography>
            </Card>
            <Card sx={{ border: '2px solid #4CAF50', px: 2, py: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Work Control Number
              </Typography>
              <TextField
                value={formData.work_control_number}
                onChange={handleFormChange('work_control_number')}
                size="small"
                disabled={readOnly}
                sx={{ mt: 1 }}
              />
            </Card>
          </Box>
        </Box>

        <Grid container spacing={2}>
          {/* Lockout Boxes */}
          <Grid item xs={12}>
            <Card sx={{ bgcolor: '#4CAF50', color: 'white', mb: 1 }}>
              <CardContent sx={{ py: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Lockout Box(s)
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Recipient Lock Number(s)
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  value={formData.lockout_boxes}
                  onChange={handleFormChange('lockout_boxes')}
                  disabled={readOnly}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  value={formData.recipient_lock_numbers}
                  onChange={handleFormChange('recipient_lock_numbers')}
                  disabled={readOnly}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Recipient Information */}
          <Grid item xs={12}>
            <Card sx={{ bgcolor: '#4CAF50', color: 'white', mb: 1 }}>
              <CardContent sx={{ py: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Recipient Name</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Company</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Contact Number</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Competency Expiry</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Autocomplete
                  options={authorizedRecipients}
                  getOptionLabel={(option) => option.user_name || ''}
                  value={authorizedRecipients.find(u => u.user_name === formData.recipient_name) || null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setFormData(prev => ({
                        ...prev,
                        recipient_name: newValue.user_name,
                        recipient_company: newValue.user_department || '',
                        recipient_contact_number: newValue.user_contact || '',
                        // Auto-populate competency expiry if available
                        recipient_competency_expiry: newValue.competency_expiry ? new Date(newValue.competency_expiry) : null
                      }));
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        recipient_name: '',
                        recipient_company: '',
                        recipient_contact_number: '',
                        recipient_competency_expiry: null
                      }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      placeholder="Select authorized recipient"
                      disabled={readOnly}
                    />
                  )}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  value={formData.recipient_company}
                  onChange={handleFormChange('recipient_company')}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  value={formData.recipient_contact_number}
                  onChange={handleFormChange('recipient_contact_number')}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={3}>
                <DatePicker
                  value={formData.recipient_competency_expiry}
                  onChange={handleDateChange('recipient_competency_expiry')}
                  disabled={readOnly}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Station and Equipment */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card sx={{ bgcolor: '#4CAF50', color: 'white', mb: 1 }}>
                  <CardContent sx={{ py: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Station</Typography>
                  </CardContent>
                </Card>
                <TextField
                  fullWidth
                  value={formData.station}
                  onChange={handleFormChange('station')}
                  disabled={readOnly}
                />
              </Grid>
              
              {/* Equipment Selection */}
              <Grid item xs={12}>
                <Card sx={{ bgcolor: '#2196F3', color: 'white', mb: 1 }}>
                  <CardContent sx={{ py: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      🔧 Equipment Selection (Auto-populates Isolations)
                    </Typography>
                  </CardContent>
                </Card>
                <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Site</InputLabel>
                        <Select
                          value={selectedSite || ''}
                          onChange={(e) => {
                            setSelectedSite(Number(e.target.value) || null);
                          }}
                          disabled={readOnly}
                        >
                          {sites.map(site => (
                            <MenuItem key={site.id} value={site.id}>
                              {site.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Location (Coming Soon)</InputLabel>
                        <Select
                          value=""
                          disabled={true}
                        >
                          <MenuItem value="">
                            <em>Location filtering not yet implemented</em>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Equipment</InputLabel>
                        <Select
                          multiple
                          value={selectedEquipmentIds}
                          onChange={(e) => {
                            const ids = typeof e.target.value === 'string' 
                              ? [Number(e.target.value)] 
                              : (e.target.value as number[]);
                            handleEquipmentSelection(ids);
                          }}
                          disabled={readOnly || !selectedSite}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {(selected as number[]).map((id) => {
                                const eq = siteEquipment.find(e => e.id === id);
                                return (
                                  <Chip 
                                    key={id} 
                                    label={eq ? `${eq.kks_code}` : id} 
                                    size="small"
                                    color={eq?.requires_isolation ? 'error' : 'default'}
                                  />
                                );
                              })}
                            </Box>
                          )}
                        >
                          {siteEquipment
                            // Equipment is already filtered by selected site in useSiteEquipment
                            // Location filtering would require backend API enhancement to include location_id
                            .map(equipment => (
                              <MenuItem key={equipment.id} value={equipment.id}>
                                <Checkbox checked={selectedEquipmentIds.includes(equipment.id)} />
                                <Box sx={{ ml: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {equipment.kks_code}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {equipment.name}
                                    {equipment.requires_isolation && (
                                      <Chip size="small" label="Isolation Required" color="error" sx={{ ml: 1 }} />
                                    )}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  
                  {loadingIsolations && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      <Typography variant="body2">Analyzing isolations...</Typography>
                    </Box>
                  )}
                  
                  {isolationAnalysis && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        ✅ Isolation Analysis Complete
                      </Typography>
                      <Typography variant="caption">
                        Found {isolationAnalysis.isolation_methods_count} isolation methods for {isolationAnalysis.equipment_analyzed} equipment items.
                        Complexity: {isolationAnalysis.analysis_summary.isolation_complexity}
                      </Typography>
                    </Alert>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Card sx={{ bgcolor: '#4CAF50', color: 'white', mb: 1 }}>
                  <CardContent sx={{ py: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Equipment to be Worked On</Typography>
                  </CardContent>
                </Card>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.equipment_to_be_worked_on}
                  onChange={handleFormChange('equipment_to_be_worked_on')}
                  disabled={readOnly}
                  placeholder="Select equipment above to auto-populate, or enter manually"
                />
              </Grid>

              <Grid item xs={8}>
                <Card sx={{ bgcolor: '#4CAF50', color: 'white', mb: 1 }}>
                  <CardContent sx={{ py: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Work to be Done</Typography>
                  </CardContent>
                </Card>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={formData.work_to_be_done}
                  onChange={handleFormChange('work_to_be_done')}
                  disabled={readOnly}
                />
              </Grid>

              <Grid item xs={4}>
                <Card sx={{ bgcolor: '#4CAF50', color: 'white', mb: 1 }}>
                  <CardContent sx={{ py: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>But Note That</Typography>
                  </CardContent>
                </Card>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={formData.but_note_that}
                  onChange={handleFormChange('but_note_that')}
                  disabled={readOnly}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Issue Section */}
        <Card sx={{ bgcolor: '#E8F5E8', p: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Issue
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The equipment specified has been removed from service, and the Issuer Applied Safety Measures listed are in place.
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Issuer Name</Typography>
              <TextField
                fullWidth
                value={formData.issuer_name}
                onChange={handleFormChange('issuer_name')}
                disabled={readOnly}
                size="small"
              />
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Competency Expiry</Typography>
              <DatePicker
                value={formData.issuer_competency_expiry}
                onChange={handleDateChange('issuer_competency_expiry')}
                disabled={readOnly}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            <Grid item xs={4}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Date</Typography>
                  <DatePicker
                    value={formData.issue_date}
                    onChange={handleDateChange('issue_date')}
                    disabled={readOnly}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Time</Typography>
                  <TimePicker
                    value={formData.issue_time}
                    onChange={handleDateChange('issue_time')}
                    disabled={readOnly}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </Grid>
              </Grid>
            </Grid>
            
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Issuer Signature</Typography>
              <TextField
                fullWidth
                disabled={readOnly}
                placeholder="Digital signature or initials"
                size="small"
              />
            </Grid>
            
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Issue Communicated Via</Typography>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.issue_communication_hand}
                      onChange={handleFormChange('issue_communication_hand')}
                      disabled={readOnly}
                    />
                  }
                  label="Hand"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.issue_communication_verbal}
                      onChange={handleFormChange('issue_communication_verbal')}
                      disabled={readOnly}
                    />
                  }
                  label="Verbal"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.issue_communication_digital}
                      onChange={handleFormChange('issue_communication_digital')}
                      disabled={readOnly}
                    />
                  }
                  label="Digital"
                />
              </FormGroup>
            </Grid>
          </Grid>
        </Card>

        {/* Acceptance Section */}
        <Card sx={{ bgcolor: '#E8F5E8', p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Acceptance
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            I am satisfied that the safety measures listed on the following pages are in place, and I understand the equipment covered by this permit.
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Recipient Signature</Typography>
              <TextField
                fullWidth
                disabled={readOnly}
                placeholder="Digital signature or initials"
                size="small"
              />
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Date</Typography>
              <DatePicker
                disabled={readOnly}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Time</Typography>
              <TimePicker
                disabled={readOnly}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
          </Grid>
        </Card>
      </Paper>

      {/* Page 2: Issuer Applied Safety Measures */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
              STAY
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#FF6B35' }}>
              LIVE
            </Typography>
            <Typography variant="body2" sx={{ ml: 1 }}>
              Electrical Industry Health & Safety Group
            </Typography>
          </Box>
          <Card sx={{ bgcolor: 'lightgray', px: 2, py: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
              Issuer Applied Safety Measures
            </Typography>
          </Card>
          <Card sx={{ border: '2px solid black', px: 2, py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Work Control Number
            </Typography>
            <Typography variant="body1">{formData.work_control_number}</Typography>
          </Card>
        </Box>

        <TableContainer>
          <Table size="small" sx={{ border: '1px solid black' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold', width: '3%' }}></TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold', width: '12%' }}>
                  Equipment Code
                </TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold', width: '20%' }}>
                  Isolation Point
                </TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold', width: '20%' }}>
                  Isolation Method
                </TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold', width: '8%' }}>
                  Lock Number
                </TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold', width: '8%' }}>
                  Isolation Check (Initial)
                </TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold', width: '6%' }}>
                  Issuer (Initial)
                </TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold', width: '6%' }}>
                  Recipient (Initial)
                </TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold', width: '8%' }}>
                  Amendments Additions
                </TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold', width: '8%' }}>
                  Amendments Deletions
                </TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold' }}>
                  Safety Measures
                </TableCell>
                <TableCell colSpan={4} sx={{ border: '1px solid black', fontWeight: 'bold', fontSize: '0.8rem' }}>
                  Issuer to list all isolation, earthing, locking, safety measures, etc applied for issue of this permit.
                </TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold', fontSize: '0.75rem' }}>
                  Isolation/Locks
                </TableCell>
                <TableCell colSpan={4} sx={{ border: '1px solid black', fontWeight: 'bold', fontSize: '0.75rem' }}>
                  Amendments
                </TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ border: '1px solid black' }}></TableCell>
                <TableCell sx={{ border: '1px solid black' }}></TableCell>
                <TableCell sx={{ border: '1px solid black' }}></TableCell>
                <TableCell sx={{ border: '1px solid black' }}></TableCell>
                <TableCell sx={{ border: '1px solid black' }}></TableCell>
                <TableCell sx={{ border: '1px solid black' }}></TableCell>
                <TableCell sx={{ border: '1px solid black', fontSize: '0.75rem' }}>Issuer (Initial)</TableCell>
                <TableCell sx={{ border: '1px solid black', fontSize: '0.75rem' }}>Recipient (Initial)</TableCell>
                <TableCell sx={{ border: '1px solid black', fontSize: '0.75rem' }}>Issuer (Initial)</TableCell>
                <TableCell sx={{ border: '1px solid black', fontSize: '0.75rem' }}>Recipient (Initial)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {issuerSafetyMeasures.map((record, index) => (
                <TableRow key={record.id}>
                  <TableCell sx={{ border: '1px solid black', textAlign: 'center', fontWeight: 'bold' }}>
                    {record.sequence_number}
                  </TableCell>
                  <TableCell sx={{ border: '1px solid black', p: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={record.equipment_code}
                      onChange={(e) => updateIssuerSafetyMeasure(index, 'equipment_code', e.target.value)}
                      disabled={readOnly}
                      sx={{ '& .MuiOutlinedInput-root': { border: 'none' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ border: '1px solid black', p: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={record.isolation_point}
                      onChange={(e) => updateIssuerSafetyMeasure(index, 'isolation_point', e.target.value)}
                      disabled={readOnly}
                      sx={{ '& .MuiOutlinedInput-root': { border: 'none' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ border: '1px solid black', p: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={record.isolation_method}
                      onChange={(e) => updateIssuerSafetyMeasure(index, 'isolation_method', e.target.value)}
                      disabled={readOnly}
                      sx={{ '& .MuiOutlinedInput-root': { border: 'none' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ border: '1px solid black', p: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={record.lock_number}
                      onChange={(e) => updateIssuerSafetyMeasure(index, 'lock_number', e.target.value)}
                      disabled={readOnly}
                      sx={{ '& .MuiOutlinedInput-root': { border: 'none' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ border: '1px solid black', p: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={record.isolation_check_initial}
                      onChange={(e) => updateIssuerSafetyMeasure(index, 'isolation_check_initial', e.target.value)}
                      disabled={readOnly}
                      inputProps={{ maxLength: 10 }}
                      sx={{ '& .MuiOutlinedInput-root': { border: 'none' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ border: '1px solid black', p: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={record.issuer_initial}
                      onChange={(e) => updateIssuerSafetyMeasure(index, 'issuer_initial', e.target.value)}
                      disabled={readOnly}
                      inputProps={{ maxLength: 10 }}
                      sx={{ '& .MuiOutlinedInput-root': { border: 'none' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ border: '1px solid black', p: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={record.recipient_initial}
                      onChange={(e) => updateIssuerSafetyMeasure(index, 'recipient_initial', e.target.value)}
                      disabled={readOnly}
                      inputProps={{ maxLength: 10 }}
                      sx={{ '& .MuiOutlinedInput-root': { border: 'none' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ border: '1px solid black', p: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={record.amendment_issuer_initial}
                      onChange={(e) => updateIssuerSafetyMeasure(index, 'amendment_issuer_initial', e.target.value)}
                      disabled={readOnly}
                      inputProps={{ maxLength: 10 }}
                      sx={{ '& .MuiOutlinedInput-root': { border: 'none' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ border: '1px solid black', p: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={record.amendment_recipient_initial}
                      onChange={(e) => updateIssuerSafetyMeasure(index, 'amendment_recipient_initial', e.target.value)}
                      disabled={readOnly}
                      inputProps={{ maxLength: 10 }}
                      sx={{ '& .MuiOutlinedInput-root': { border: 'none' } }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Page 3: Recipient Applied Safety Measures */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
              STAY
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#FF6B35' }}>
              LIVE
            </Typography>
            <Typography variant="body2" sx={{ ml: 1 }}>
              Electrical Industry Health & Safety Group
            </Typography>
          </Box>
          <Card sx={{ bgcolor: 'lightgray', px: 2, py: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
              Recipient Applied Safety Measures
            </Typography>
          </Card>
          <Card sx={{ border: '2px solid black', px: 2, py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Work Control Number
            </Typography>
            <Typography variant="body1">{formData.work_control_number}</Typography>
          </Card>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Record all the Issuer Agreed, Recipient Applied Safety Measures and Plant Status Control Measures, 
            in accordance with the <strong>StayLive</strong> Work Control Procedures.
          </Typography>
        </Alert>

        <TableContainer>
          <Table size="small" sx={{ border: '1px solid black' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold', width: '3%' }}></TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold', width: '25%' }}>
                  Isolation Point
                </TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold', width: '25%' }}>
                  Isolation Method
                </TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold', width: '8%' }}>
                  Issuer Agreed (Initial)
                </TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold', width: '8%' }}>
                  Lock No.
                </TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold', width: '8%' }}>
                  Applied (Initial)
                </TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold', width: '8%' }}>
                  Removed (Initial)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recipientSafetyMeasures.map((record, index) => (
                <TableRow key={record.id}>
                  <TableCell sx={{ border: '1px solid black', textAlign: 'center', fontWeight: 'bold' }}>
                    {record.sequence_number}
                  </TableCell>
                  <TableCell sx={{ border: '1px solid black', p: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={record.isolation_point}
                      onChange={(e) => updateRecipientSafetyMeasure(index, 'isolation_point', e.target.value)}
                      disabled={readOnly}
                      sx={{ '& .MuiOutlinedInput-root': { border: 'none' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ border: '1px solid black', p: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={record.isolation_method}
                      onChange={(e) => updateRecipientSafetyMeasure(index, 'isolation_method', e.target.value)}
                      disabled={readOnly}
                      sx={{ '& .MuiOutlinedInput-root': { border: 'none' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ border: '1px solid black', p: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={record.issuer_agreed_initial}
                      onChange={(e) => updateRecipientSafetyMeasure(index, 'issuer_agreed_initial', e.target.value)}
                      disabled={readOnly}
                      inputProps={{ maxLength: 10 }}
                      sx={{ '& .MuiOutlinedInput-root': { border: 'none' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ border: '1px solid black', p: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={record.lock_number}
                      onChange={(e) => updateRecipientSafetyMeasure(index, 'lock_number', e.target.value)}
                      disabled={readOnly}
                      sx={{ '& .MuiOutlinedInput-root': { border: 'none' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ border: '1px solid black', p: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={record.applied_initial}
                      onChange={(e) => updateRecipientSafetyMeasure(index, 'applied_initial', e.target.value)}
                      disabled={readOnly}
                      inputProps={{ maxLength: 10 }}
                      sx={{ '& .MuiOutlinedInput-root': { border: 'none' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ border: '1px solid black', p: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={record.removed_initial}
                      onChange={(e) => updateRecipientSafetyMeasure(index, 'removed_initial', e.target.value)}
                      disabled={readOnly}
                      inputProps={{ maxLength: 10 }}
                      sx={{ '& .MuiOutlinedInput-root': { border: 'none' } }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Bottom Certification */}
        <Card sx={{ bgcolor: '#E8F5E8', p: 2, mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Recipient:</strong> I have checked and I am satisfied that all safety measures applied by my work party have been removed.
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Recipient Name</Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.recipient_name}
                disabled={readOnly}
              />
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Recipient Signature</Typography>
              <TextField
                fullWidth
                size="small"
                disabled={readOnly}
                placeholder="Digital signature"
              />
            </Grid>
            <Grid item xs={2}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Date</Typography>
              <DatePicker
                disabled={readOnly}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            <Grid item xs={2}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Time</Typography>
              <TimePicker
                disabled={readOnly}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
          </Grid>
        </Card>
      </Paper>

      {/* Page 4: Return & Cancel */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
              STAY
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#FF6B35' }}>
              LIVE
            </Typography>
            <Typography variant="body2" sx={{ ml: 1 }}>
              Electrical Industry Health & Safety Group
            </Typography>
          </Box>
          <Card sx={{ bgcolor: 'lightgray', px: 2, py: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
              Access Permit – Return & Cancel
            </Typography>
          </Card>
          <Card sx={{ border: '2px solid black', px: 2, py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Work Control Number
            </Typography>
            <Typography variant="body1">{formData.work_control_number}</Typography>
          </Card>
        </Box>

        {/* Return by Recipient */}
        <Card sx={{ border: '1px solid black', mb: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              Return by Recipient
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              This permit is returned. My work party has been informed in accordance with the StayLive Work Control Procedures.
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Return Reason</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.return_reason}
                  onChange={handleFormChange('return_reason')}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Recipient Signature</Typography>
                <TextField
                  fullWidth
                  size="small"
                  disabled={readOnly}
                  placeholder="Digital signature"
                />
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Date</Typography>
                <DatePicker
                  disabled={readOnly}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Time</Typography>
                <TimePicker
                  disabled={readOnly}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Re-issue */}
        <Card sx={{ border: '1px solid black', mb: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              Re-issue
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 2 }}>Issuer</Typography>
                <Typography variant="caption" sx={{ mb: 2, display: 'block' }}>
                  The equipment specified has been removed from service and the Issuer Applied Safety Measures listed are in place.
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Name (Print)</Typography>
                <TextField fullWidth size="small" disabled={readOnly} />
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Signature</Typography>
                <TextField fullWidth size="small" disabled={readOnly} />
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Competency Expiry</Typography>
                <DatePicker
                  disabled={readOnly}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Date</Typography>
                <DatePicker
                  disabled={readOnly}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Time</Typography>
                <TimePicker
                  disabled={readOnly}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 2 }}>Recipient</Typography>
                <Typography variant="caption" sx={{ mb: 2, display: 'block' }}>
                  I am satisfied that the safety measures listed on the following pages are in place, and I understand the equipment covered by this permit.
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Name (Print)</Typography>
                <TextField fullWidth size="small" disabled={readOnly} />
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Signature</Typography>
                <TextField fullWidth size="small" disabled={readOnly} />
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Date</Typography>
                <DatePicker
                  disabled={readOnly}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Time</Typography>
                <TimePicker
                  disabled={readOnly}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Equipment Modifications */}
        <Card sx={{ border: '1px solid black', mb: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              Equipment Modifications
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={formData.equipment_modifications_made}
              onChange={handleFormChange('equipment_modifications_made')}
              disabled={readOnly}
              placeholder="List any modifications made to equipment"
            />
          </CardContent>
        </Card>

        {/* Cancellation */}
        <Card sx={{ border: '1px solid black', mb: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              Cancellation
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              This permit is cancelled.
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Issuer Name</Typography>
                <TextField fullWidth size="small" disabled={readOnly} />
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Issuer Signature</Typography>
                <TextField fullWidth size="small" disabled={readOnly} />
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Date</Typography>
                <DatePicker
                  disabled={readOnly}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Time</Typography>
                <TimePicker
                  disabled={readOnly}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Return to Service */}
        <Card sx={{ border: '1px solid black' }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={3}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Return to Service:
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.equipment_returned_to_service}
                        onChange={handleFormChange('equipment_returned_to_service')}
                        disabled={readOnly}
                      />
                    }
                    label="YES"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!formData.equipment_returned_to_service}
                        onChange={(e) => setFormData(prev => ({ ...prev, equipment_returned_to_service: !e.target.checked }))}
                        disabled={readOnly}
                      />
                    }
                    label="NO"
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={7}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  List any Restrictions: (If "No", list reason why not and any restrictions, in the box below)
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.return_to_service_restrictions}
                  onChange={handleFormChange('return_to_service_restrictions')}
                  disabled={readOnly}
                  placeholder="Enter any restrictions or reasons"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Paper>

      {/* Page 5: Work Party */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
              STAY
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#FF6B35' }}>
              LIVE
            </Typography>
            <Typography variant="body2" sx={{ ml: 1 }}>
              Electrical Industry Health & Safety Group
            </Typography>
          </Box>
          <Card sx={{ bgcolor: 'lightgray', px: 2, py: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
              Work Party
            </Typography>
          </Card>
          <Card sx={{ border: '2px solid black', px: 2, py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Work Control Number
            </Typography>
            <Typography variant="body1">{formData.work_control_number}</Typography>
          </Card>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Work party supervisor should be indicated by an <strong>(S)</strong> after the Supervisors name (where appropriate)
          </Typography>
        </Alert>

        <TableContainer>
          <Table size="small" sx={{ border: '1px solid black' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold', width: '3%' }}></TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold', width: '15%', textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    I understand the equipment as detailed is available to work on.
                  </Typography>
                </TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold', width: '15%', textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    I understand the equipment is no longer available to work on.
                  </Typography>
                </TableCell>
                <TableCell colSpan={3} sx={{ border: '1px solid black', fontWeight: 'bold', textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Sign On
                  </Typography>
                </TableCell>
                <TableCell colSpan={3} sx={{ border: '1px solid black', fontWeight: 'bold', textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Sign Off
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ border: '1px solid black' }}></TableCell>
                <TableCell sx={{ border: '1px solid black' }}></TableCell>
                <TableCell sx={{ border: '1px solid black' }}></TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold' }}>Sign On</TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold' }}>a</TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold' }}>b</TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold' }}>Sign Off</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workPartyRecords.map((record, index) => (
                <TableRow key={record.id}>
                  <TableCell sx={{ border: '1px solid black', textAlign: 'center', fontWeight: 'bold' }}>
                    {record.sequence_letter}
                  </TableCell>
                  <TableCell sx={{ border: '1px solid black' }}></TableCell>
                  <TableCell sx={{ border: '1px solid black' }}></TableCell>
                  <TableCell sx={{ border: '1px solid black', p: 0 }}>
                    <DatePicker
                      value={record.sign_on_date}
                      onChange={(date) => updateWorkPartyRecord(index, 'sign_on_date', date)}
                      disabled={readOnly}
                      slotProps={{ 
                        textField: { 
                          fullWidth: true, 
                          size: 'small',
                          sx: { '& .MuiOutlinedInput-root': { border: 'none' } }
                        } 
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ border: '1px solid black', p: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={record.name}
                      onChange={(e) => updateWorkPartyRecord(index, 'name', e.target.value)}
                      disabled={readOnly}
                      placeholder={record.is_supervisor ? 'Name (S)' : 'Name'}
                      sx={{ '& .MuiOutlinedInput-root': { border: 'none' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ border: '1px solid black', p: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={record.sign_on_signature}
                      onChange={(e) => updateWorkPartyRecord(index, 'sign_on_signature', e.target.value)}
                      disabled={readOnly}
                      sx={{ '& .MuiOutlinedInput-root': { border: 'none' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ border: '1px solid black', textAlign: 'center', fontWeight: 'bold' }}>
                    {record.sequence_letter}
                  </TableCell>
                  <TableCell sx={{ border: '1px solid black' }}></TableCell>
                  <TableCell sx={{ border: '1px solid black', p: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={record.sign_off_signature}
                      onChange={(e) => updateWorkPartyRecord(index, 'sign_off_signature', e.target.value)}
                      disabled={readOnly}
                      sx={{ '& .MuiOutlinedInput-root': { border: 'none' } }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Page 6: Access Permit Cover */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
              STAY
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#FF6B35' }}>
              LIVE
            </Typography>
            <Typography variant="body2" sx={{ ml: 1 }}>
              Electrical Industry Health & Safety Group
            </Typography>
          </Box>
          
          <Card sx={{ bgcolor: '#4CAF50', color: 'white', py: 4, mb: 4 }}>
            <Typography variant="h2" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
              ACCESS<br />PERMIT
            </Typography>
          </Card>
          
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
            REPORT TO:
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ border: '2px solid black', p: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 2 }}>
                NAME OF ACCESS PERMIT RECIPIENT:
              </Typography>
              <Typography variant="h6" sx={{ textAlign: 'center', minHeight: '40px', border: '1px solid #ddd', p: 1 }}>
                {formData.recipient_name}
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ border: '2px solid black', p: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 2 }}>
                CONTACT PHONE NUMBER:
              </Typography>
              <Typography variant="h6" sx={{ textAlign: 'center', minHeight: '40px', border: '1px solid #ddd', p: 1 }}>
                {formData.recipient_contact_number}
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ border: '2px solid black', p: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 2 }}>
                WORK CONTROL NUMBER:
              </Typography>
              <Typography variant="h6" sx={{ textAlign: 'center', minHeight: '40px', border: '1px solid #ddd', p: 1 }}>
                {formData.work_control_number}
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ border: '2px solid black', p: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 2 }}>
                EQUIPMENT BEING WORKED ON:
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  textAlign: 'center', 
                  minHeight: '80px', 
                  border: '1px solid #ddd', 
                  p: 2,
                  whiteSpace: 'pre-line'
                }}
              >
                {formData.equipment_to_be_worked_on}
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ border: '2px solid black', p: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 2 }}>
                WORK BEING DONE:
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  textAlign: 'center', 
                  minHeight: '80px', 
                  border: '1px solid #ddd', 
                  p: 2,
                  whiteSpace: 'pre-line'
                }}
              >
                {formData.work_to_be_done}
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Completion Summary */}
      <Alert severity="success" sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          ✅ Complete StayLive Access Permit Implementation
        </Typography>
        <Typography variant="body2">
          All 6 pages of the Access Permit template are now implemented with full isolation system integration.
          Equipment selection automatically populates safety measures from your comprehensive isolation database.
        </Typography>
      </Alert>
    </Box>
  );
};

export default AccessPermitForm;